'use client';

import { createElement, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { toast } from '@/lib/toast';
import { ValeAIMark } from '@/components/icons/vale-ai-mark';
import {
  getAnalysisProgressAction,
  previewChangesAction
} from '@/lib/services/ai-analysis/actions';
import {
  describePreviewChanges,
  getPreviewChangesCounts
} from '@/lib/services/ai-analysis/utils';
import { MATTER_ROUTES } from '@/lib/constants/routes';
import {
  usePendingAiReviewStore,
  type AnalysisTriggerSource
} from '@/lib/stores/pending-ai-review-store';

/**
 * Query key for the matter's AI analysis progress poll. Exposed so call
 * sites (e.g. typed-dialog upload handlers) can invalidate it right after
 * `POST /ai-analysis/consolidate` returns, so the poll starts immediately
 * rather than on the next 5s tick.
 */
export const aiAnalysisProgressKey = (matterId: number) =>
  ['ai-analysis', 'progress', matterId] as const;

const POLL_INTERVAL_MS = 5_000;

// LF-4048: how long the in-flight loading toast may spin before we morph it into a
// "taking longer than usual" message. The loading toast has duration:Infinity and
// only resolves on the active → completed/failed transition; when the backend run
// stalls (stays processing/consolidating), that transition never arrives, so without
// this fallback the lawyer is left watching an indefinite spinner with no feedback.
// 2 min matches the point reporters flag the spinner as "stuck" — a normal run
// finishes in well under a minute.
const ANALYSIS_STALL_TIMEOUT_MS = 120_000;

/**
 * Matter-layout-level hook that polls the AI analysis progress endpoint
 * every 5s while a run is in flight (`status` ∈ `'processing'`,
 * `'consolidating'`). On the active → `completed` transition it fires a
 * best-effort toast and triggers the pending-review banner to surface.
 *
 * Lives at the matter layout so it survives in-matter tab navigation: a
 * lawyer who uploads on the Documents tab, switches to Tasks, then comes
 * back, still gets the toast / sees the banner.
 *
 * Trigger-source tracking (`'core'` vs `'additional-opt-in'`) drives the
 * nothing-found copy: a typed-modal upload that yields no new entities
 * gets a subtle toast ("nothing new to add"); an Additional Documents
 * opt-in that yields nothing stays silent.
 */
export function useMatterAiAnalysisPolling(matterId: number) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const requestReview = usePendingAiReviewStore((s) => s.requestReview);
  const triggerSource = usePendingAiReviewStore((s) => s.lastTriggerSource);
  const clearTriggerSource = usePendingAiReviewStore(
    (s) => s.clearTriggerSource
  );
  const inlineProgressFor = usePendingAiReviewStore((s) => s.inlineProgressFor);

  // Snapshot the last seen status so we only fire the toast on the
  // active → completed transition (not on every poll while completed).
  const lastStatusRef = useRef<string | null>(null);
  const triggerSourceRef = useRef<AnalysisTriggerSource | null>(null);
  // Pending "taking longer than usual" timer for the in-flight toast (LF-4048).
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    triggerSourceRef.current = triggerSource;
  }, [triggerSource]);

  const progressQuery = useQuery({
    queryKey: aiAnalysisProgressKey(matterId),
    queryFn: async () => {
      const result = await getAnalysisProgressAction(matterId);
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch analysis progress');
      }
      return result.data;
    },
    enabled: Number.isFinite(matterId) && matterId > 0,
    // Don't refetch on mount/focus — the poll is the only refresh trigger.
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Active poll while a run is in flight; pause otherwise.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      // `retrying` (LF-4320) is in-flight — keep polling so we observe the run
      // recover (→ processing/consolidating/completed) or finally fail.
      return data.status === 'processing' ||
        data.status === 'consolidating' ||
        data.status === 'retrying'
        ? POLL_INTERVAL_MS
        : false;
    }
  });

  /**
   * Is a surface that announces this matter's result already on screen?
   *
   * Read from the store at decision time rather than through the effect's
   * closure: this is answered after `previewChangesAction` resolves, by which
   * point the lawyer may have closed the Extract dialog or left the Documents
   * tab, and the honest answer is the one that is true when the toast would
   * appear. Both surfaces count: the Extract dialog showing its own progress
   * and review (`inlineProgressFor`), and the Documents tab's pending-review
   * banner (`resultSurfaceFor`).
   */
  const resultIsOnScreen = useCallback((id: number) => {
    const { inlineProgressFor: inline, resultSurfaceFor: surface } =
      usePendingAiReviewStore.getState();
    return inline === id || surface === id;
  }, []);

  /**
   * Take the lawyer to the review they asked for. The request is raised BEFORE
   * the navigation, so `DocumentsTabContent` meets it already set at first
   * render, which is the case it was not handling until VALE-1434.
   */
  const openReview = useCallback(
    (id: number) => {
      requestReview(id);
      router.push(MATTER_ROUTES.DOCUMENTS(id));
    },
    [requestReview, router]
  );

  useEffect(() => {
    const status = progressQuery.data?.status;
    if (!status) return;
    const previous = lastStatusRef.current;
    lastStatusRef.current = status;

    // Persistent in-flight indicator. A single loading toast (stable id) lives
    // for the whole analysis run so the lawyer knows something is happening in
    // the background; the completion/failure handlers below reuse the same id so
    // it morphs in place into the result rather than stacking a second toast.
    //
    // Guard: only show the toast if we observed the transition in this session
    // (previous !== null) OR the user explicitly triggered analysis in this
    // session (triggerSourceRef.current !== null). Without this guard, mounting
    // onto a matter whose Temporal workflow is stuck returns 'processing'
    // forever and the loading toast (duration: Infinity) never dismisses (LF-4030).
    const analysisToastId = `ai-analysis-${matterId}`;
    const isSessionInitiated =
      previous !== null || triggerSourceRef.current !== null;
    // Nothing is "in the background" while the lawyer is watching it run
    // (VALE-757). When the Extract dialog is showing progress for this matter,
    // that IS the indicator; a spinner announcing background work on top of it
    // is the ambient surface this ticket removed, re-created one layer up.
    if (inlineProgressFor === matterId) {
      toast.dismiss(analysisToastId);
    } else if (
      (status === 'processing' ||
        status === 'consolidating' ||
        status === 'retrying') &&
      isSessionInitiated
    ) {
      toast.loading('Vale AI is analysing your documents in the background…', {
        id: analysisToastId,
        icon: createElement(ValeAIMark, {
          box: 16,
          thinking: true,
          className: 'text-ai'
        }),
        cancel: {
          label: 'Dismiss',
          onClick: () => {
            if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
            toast.dismiss(analysisToastId);
          }
        }
      });

      // Stall fallback (LF-4048): if no terminal status arrives within
      // ANALYSIS_STALL_TIMEOUT_MS, morph the spinner's copy into a reassuring
      // "taking longer than usual" message so the lawyer isn't left guessing
      // whether anything is happening. Cleared by the completed/failed handlers,
      // by Dismiss, and on unmount — so it only fires while genuinely still in
      // flight. (Re)started on each in-flight transition (e.g. processing →
      // consolidating) so a run that's still making progress gets a fresh window.
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = setTimeout(() => {
        toast.loading(
          'Vale AI is still analysing your documents. This is taking longer than usual. You can keep working; we’ll update the Documents tab when it’s ready.',
          {
            id: analysisToastId,
            icon: createElement(ValeAIMark, {
              box: 16,
              thinking: true,
              className: 'text-ai'
            }),
            cancel: {
              label: 'Dismiss',
              onClick: () => toast.dismiss(analysisToastId)
            }
          }
        );
      }, ANALYSIS_STALL_TIMEOUT_MS);
    }

    // Active → completed transition. Skip the case where the consumer
    // mounts with status already `'completed'` (previous === null) — that
    // means the lawyer is returning to a stale completed run; the banner
    // surfaces it, no toast needed.
    if (
      status === 'completed' &&
      (previous === 'processing' ||
        previous === 'consolidating' ||
        previous === 'retrying')
    ) {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      const source = triggerSourceRef.current;

      void (async () => {
        const previewResult = await previewChangesAction(matterId);
        if (!previewResult.success || !previewResult.data) {
          // The banner reads the same endpoint and hides itself when it fails,
          // so on this path NOTHING would speak (VALE-1434). Say the run
          // finished and point at the tab that owns the result rather than
          // claiming one we could not read. Same `'core'` gate and same
          // on-screen check as the success path.
          if (resultIsOnScreen(matterId) || source !== 'core') {
            toast.dismiss(analysisToastId);
          } else {
            toast.info(
              'Extraction finished. We could not load the summary just now. Open the Documents tab to see the result.',
              {
                id: analysisToastId,
                action: {
                  label: 'Open documents',
                  onClick: () => router.push(MATTER_ROUTES.DOCUMENTS(matterId))
                }
              }
            );
          }
          clearTriggerSource();
          return;
        }
        const data = previewResult.data;
        const counts = getPreviewChangesCounts(data);
        const { hasNewRecords, hasUpdates } = counts;

        if (hasNewRecords || hasUpdates) {
          // No completion toast (VALE-757) WHERE A SURFACE IS ALREADY SHOWING
          // THE RESULT. This hook runs at the matter LAYOUT, so its toast fired
          // wherever the lawyer happened to be standing, the genuinely ambient
          // half of the surface Tom reported, and a duplicate of the
          // Documents-page banner that carries the same information with room to
          // say what actually changed. Where that banner (or the Extract
          // dialog's own review step) is on screen, it stays the single place a
          // result is announced, and re-adding a toast there re-creates the bug.
          //
          // Where neither is, silence is the bug (VALE-1434). A lawyer who asked
          // for extraction from the Grant tab got no result, no review and no
          // error, because the only surface that speaks lives on a tab she was
          // not on. Gated on `'core'` for exactly the reason the nothing-found
          // toast below already is: a typed-dialog extraction is a result the
          // lawyer asked for, not an announcement about work they did not
          // request. The action is what makes it useful rather than merely
          // noisy: it carries them to the review they were promised.
          if (resultIsOnScreen(matterId) || source !== 'core') {
            toast.dismiss(analysisToastId);
          } else {
            toast.info(
              `Your extraction is ready to review. ${describePreviewChanges(counts)}`,
              {
                id: analysisToastId,
                action: {
                  label: 'Review',
                  onClick: () => openReview(matterId)
                }
              }
            );
          }
        } else if (source === 'core') {
          // Typed-modal trigger source: the lawyer explicitly asked for an
          // extraction on a structured document, so silence would read as a
          // failure. This is a result they requested, not an announcement.
          toast.info('Extraction finished. Nothing new to add to the matter.', {
            id: analysisToastId
          });
        } else {
          // additional-opt-in path with nothing-found stays silent — clear the
          // in-flight loading toast rather than leaving it spinning.
          toast.dismiss(analysisToastId);
        }

        // Banner state depends on the same preview-changes call — invalidate
        // any consumer that's caching it.
        void queryClient.invalidateQueries({
          queryKey: ['ai-analysis', 'preview-changes', matterId]
        });
        clearTriggerSource();
      })();
    }

    if (status === 'failed' && previous !== 'failed') {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      const msg = progressQuery.data?.message;
      toast.error(
        msg
          ? `AI analysis failed: ${msg}`
          : 'AI analysis failed. Please try again from the Documents tab.',
        { id: analysisToastId }
      );
      clearTriggerSource();
    }
  }, [
    progressQuery.data?.status,
    progressQuery.data?.message,
    matterId,
    clearTriggerSource,
    // In the deps so closing the dialog mid-run re-evaluates and restores the
    // spinner: the work has gone back to being invisible, so the indicator is
    // wanted again.
    inlineProgressFor,
    queryClient,
    resultIsOnScreen,
    openReview,
    router
  ]);

  // Clear any pending stall timer when the hook unmounts so it can't fire (and
  // resurrect a dismissed toast) after the matter layout is gone.
  useEffect(
    () => () => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    },
    []
  );

  return {
    status: progressQuery.data?.status,
    isActive:
      progressQuery.data?.status === 'processing' ||
      progressQuery.data?.status === 'consolidating' ||
      progressQuery.data?.status === 'retrying'
  };
}
